import { useCallback, useEffect, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/lib/shadcn/ui/card";
import { Input } from "@/lib/shadcn/ui/input";
import { Label } from "@/lib/shadcn/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/lib/shadcn/ui/select";
import { useAppData } from "@/hooks/useAppData";
import {
    ApartmentUpdate,
    useAddApartmentMutation,
    useAddManagementFeeRatioMutation,
    useAddPayoutRatioMutation,
    useGetNeteraCredentialLabelsQuery,
    useGetStrOwnersQuery,
} from "@/store/slices/apartments";
import { MultiSelect } from "@/lib/shadcn/components/multi-select";
import { TagMultiSelect } from "@/lib/shadcn/components/tag-multi-select";
import { DateInput } from "@/lib/shadcn/components/date-input";
import { toast } from "@/lib/shadcn/hooks/use-toast";
import SearchableSelect from "@/lib/shadcn/components/searchable-select";
import PercentageInput from "@/lib/shadcn/components/percentageInput";
import { useNavigate } from "react-router-dom";
import { HostHubRentalSelector } from "@/lib/shadcn/components/hosthub-rental-selector";
import { NeteraAccountSelect } from "@/lib/shadcn/components/netera-account-select";
import { StrOwnerSelect, type NewStrOwner } from "@/lib/shadcn/components/str-owner-select";
import { AddressSelect } from "@/lib/shadcn/components/address-select";
import {
    isNewApartmentValid,
    missingRequiredFields,
    validateStrOwnerSelection,
} from "@/pages/new-apartment-validation";
import { SaveChangesButton } from "@/pages/save-changes-button";


export default function NewApartmentPage() {
    const [addApartment] = useAddApartmentMutation();
    const [addManagementFeeRatio] = useAddManagementFeeRatioMutation();
    const [addPayoutRatio] = useAddPayoutRatioMutation();
    const [payoutRatio, setPayoutRatio] = useState<number>(0);
    const [managementFeeRatio, setManagementFeeRatio] = useState<number>(0);
    // isExemptFromErpUpload is intentionally omitted from the initial state: it
    // is no longer UI-owned (the server derives it from the Netera selection —
    // exempt ⟺ unlinked, FR-004). The Omit-typed literal keeps every other field
    // type-checked; the widening cast supplies the response-shaped type the
    // state uses. Default Netera selection is N/A ⇒ neteraCredentialsId: null.
    const initialFormData: Omit<ApartmentUpdate, "isExemptFromErpUpload"> = {
        // No free-text `address`: the column is gone (address-normalization/FR-015)
        // and an apartment's address is the canonical string of the building it is
        // filed at. The building is PICKED, not typed (FR-009); nothing is chosen yet.
        addressId: null,
        atak: "",
        categoryId: 2,
        dateAdded: new Date(),
        dateRemoved: undefined,
        effectiveFrom: undefined,
        electricityCertificateTerminationDate: undefined,
        fireExtinguisherRefillCertificateDate: undefined,
        floor: undefined,
        electricityCompany: "",
        electricityRf: "",
        electricitySupplyId: "",
        energyCertificateIssuanceDate: undefined,
        eydapRegistryId: "",
        eydapSupplyId: "",
        groupId: -1,
        id: -1,
        imageUrl: "",
        initialTaxisRentalAgreement: "",
        insuranceCompany: "",
        insuranceContractId: "",
        internetCompany: "",
        internetLine: "",
        internetPassword: "",
        internetUsername: "",
        // isExemptFromErpUpload is no longer UI-owned: the server derives it from
        // the Netera selection (exempt ⟺ unlinked, FR-004). Default N/A ⇒ null.
        neteraCredentialsId: null,
        // STR participation is add-only (FR-012). Single-column model (D-R1/D-R2):
        // there is no wire "subject" flag — STR-exempt ⟺ `strOwnerId == null`. The
        // checkbox is pure UI state (`strSubject` below); `strOwnerId` is pre-selected
        // to the configured default once the str-owners query lands (AS-004), and
        // stays null until then / when not subject (AS-007).
        strOwnerId: null,
        latestRentalTaxisAgreement: "",
        municipalitySectionId: -1,
        name: "",
        numberOfBeds: 0,
        ownerGroupId: -1,
        ownerIds: [],
        shortName: "",
        shortTermPropertyId: 0,
        shortTermRegistryId: "",
        squareMeters: undefined,
        tagIds: [],
        disinfectionDate: undefined,
        disinfectionCertificateNumber: "",
        checkInFeeRate: 0.065,
    };
    const [formData, setFormData] = useState<ApartmentUpdate>(initialFormData as ApartmentUpdate);
    const navigate = useNavigate();
    const [apartmentOwners, setApartmentOwners] = useState<number[]>([]);
    const { ownerOptions, ownerIdMap, groupIdMap, ownerGroupOptions } = useAppData();
    const { data: neteraLabels = [], isError: neteraLabelsError } = useGetNeteraCredentialLabelsQuery();
    const { data: strOwnersData, isLoading: strOwnersLoading, isError: strOwnersError } = useGetStrOwnersQuery();
    const [strError, setStrError] = useState<string | null>(null);
    // The "Subject to STR upload" checkbox is pure UI state (D-R2): checked ⟺ an
    // owner will be sent. Nothing STR-participation-related goes on the wire except
    // `strOwnerId` / `newStrOwner`; the server treats no-owner as STR-exempt.
    const [strSubject, setStrSubject] = useState(true);
    // The inline-created owner (FR-002), null unless "＋ New owner" is chosen. Kept
    // outside `formData` since it isn't an apartment field — it's sent alongside.
    const [newStrOwner, setNewStrOwner] = useState<NewStrOwner | null>(null);

    // AS-004: pre-select the configured default owner once, the first time the
    // str-owners query lands, while the apartment is still subject and no owner
    // has been chosen. One-shot via a ref so a later manual clear / re-check
    // isn't clobbered by a re-run (avoids the init-vs-zeroing effect race).
    const preselectedOwnerRef = useRef(false);
    useEffect(() => {
        if (preselectedOwnerRef.current || !strOwnersData) {
            return;
        }
        if (!strSubject || formData.strOwnerId != null) {
            return;
        }
        // Only pre-select when the configured default resolves to an offered owner;
        // an unset/stale default leaves no owner chosen, so the operator must pick
        // one or add a new one (the EC-002 guard path).
        if (!strOwnersData.owners.some((o) => o.id === strOwnersData.defaultOwnerId)) {
            return;
        }
        preselectedOwnerRef.current = true;
        // One-shot default-owner seeding from the query — the established pattern
        // for initialising form state from async data (see booking-pace et al.).
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setFormData((prev) => ({ ...prev, strOwnerId: strOwnersData.defaultOwnerId }));
    }, [strOwnersData, strSubject, formData.strOwnerId]);

    const handleInputChange = useCallback(<T extends keyof ApartmentUpdate>(field: T, value: ApartmentUpdate[T]) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    }, []);

    const handleSaveChanges = useCallback(async () => {
        // EC-001/EC-002/EC-003: the STR card's submit-blocking guards, in order.
        const strValidationError = validateStrOwnerSelection(strSubject, formData, newStrOwner, strOwnersData);
        if (strValidationError != null) {
            setStrError(strValidationError);
            return;
        }
        setStrError(null);

        const startOfMonth = new Date();
        startOfMonth.setDate(1);
        try {
            const newApartment = {
                ...formData,
                ownerIds: apartmentOwners,
                // An inline owner travels as `newStrOwner`; the id is resolved server-side.
                ...(newStrOwner != null ? { newStrOwner, strOwnerId: null } : {}),
            };

            const addApartmentResponse = await addApartment(newApartment).unwrap();

            await addPayoutRatio({
                id: -1,
                apartmentId: addApartmentResponse.id,
                fromDate: startOfMonth,
                payoutRatio: payoutRatio / 100,
            }).unwrap();

            await addManagementFeeRatio({
                id: -1,
                apartmentId: addApartmentResponse.id,
                fromDate: startOfMonth,
                managementFeeRatio: managementFeeRatio / 100,
            }).unwrap();

            toast({
                title: "Success",
                description: "New apartment added successfully",
                variant: "success",
            });
            setTimeout(() => {
                navigate("/admin/apartments");
            }, 1000);
        } catch (error) {
            // An in-flight mutation aborted because the component unmounted
            // (e.g. the user navigated away mid-save) is not a real failure —
            // don't toast an error or log it.
            if ((error as { name?: string } | null)?.name === "AbortError") {
                return;
            }
            toast({
                title: "Error",
                description: "Failed to add new apartment",
                variant: "destructive",
            });
            console.error("Failed to add new apartment:", error);
        }
    }, [strSubject, formData, newStrOwner, strOwnersData, apartmentOwners, addApartment, addPayoutRatio, payoutRatio, addManagementFeeRatio, managementFeeRatio, navigate, setStrError]);

    const saveBlockedReason = isNewApartmentValid(formData, apartmentOwners, payoutRatio, managementFeeRatio)
        ? undefined
        : `Cannot save yet — still required: ${
              missingRequiredFields(formData, apartmentOwners, payoutRatio, managementFeeRatio).join(", ") ||
              "some apartment details"
          }.`;

    return (
        <div className="container mx-auto py-6 space-y-8">
            <div className="bg-white rounded-lg shadow-sm">
                <div className="p-6">
                    <div className="flex flex-col md:flex-row gap-8"></div>
                </div>
            </div>

            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold">New Apartment</h1>
                    <p className="text-gray-500">Manage apartment details and settings</p>
                </div>
                <div className={"space-x-2"}>
                    <SaveChangesButton reasonId="new-apartment-save-blocked-top" reason={saveBlockedReason} onClick={handleSaveChanges} />
                </div>
            </div>

            <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* General Information */}
                    <Card>
                        <CardHeader>
                            <CardTitle>General Information</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="grid grid-cols-1  gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="name">Apartment Name*</Label>
                                    <Input id="name" value={formData.name} onChange={(e) => handleInputChange("name", e.target.value)} />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="shortName">Short Name*</Label>
                                    <Input id="shortName" value={formData.shortName} onChange={(e) => handleInputChange("shortName", e.target.value)} />
                                </div>
                            </div>

                            <div className="grid grid-cols-5 space-x-6">
                                <div className="space-y-2 col-span-1">
                                    <Label htmlFor="beds">N. Bedrooms*</Label>
                                    <Input
                                        id="beds"
                                        type="number"
                                        value={formData.numberOfBeds}
                                        onChange={(e) => handleInputChange("numberOfBeds", Number(e.target.value))}
                                    />
                                </div>
                                <div className="space-y-2 col-span-1">
                                    <Label htmlFor="squareMeters">Sq. Meters</Label>
                                    <Input
                                        id="squareMeters"
                                        type="number"
                                        value={formData.squareMeters}
                                        onChange={(e) => handleInputChange("squareMeters", Number(e.target.value))}
                                    />
                                </div>
                                <div className="space-y-2 col-span-1">
                                    <Label htmlFor="floor">Floor</Label>
                                    <Input
                                        id="floor"
                                        type="number"
                                        value={formData.floor ? formData.floor : ""}
                                        onChange={(e) => handleInputChange("floor", Number(e.target.value))}
                                    />
                                </div>
                                <div className="space-y-2 col-span-2">
                                    <Label htmlFor="dateAdded">Went Live On*</Label>
                                    <DateInput
                                        id="dateAdded"
                                        value={formData.dateAdded}
                                        onChange={(date) => handleInputChange("dateAdded", date || new Date())}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                {/* The municipality section is no longer an apartment field
                                    (address-normalization/FR-020): it belongs to the building, so it is
                                    chosen once, on the address, and every apartment there inherits it. */}
                                <div className="col-span-1 space-y-2">
                                    <Label htmlFor="address">Address*</Label>
                                    <AddressSelect
                                        id="address"
                                        required
                                        value={formData.addressId ?? null}
                                        onChange={(addressId) => handleInputChange("addressId", addressId)}
                                    />
                                </div>
                                <PercentageInput id="payoutRatio" label="Payout Ratio" value={payoutRatio} onChange={setPayoutRatio} required={true} />
                                <PercentageInput
                                    id="managementFeeRatio"
                                    label="Management Fee Ratio"
                                    value={managementFeeRatio}
                                    onChange={setManagementFeeRatio}
                                    required={true}
                                />
                                <PercentageInput
                                    id="checkInFeeRate"
                                    label="Check-in Fee Rate"
                                    value={(formData.checkInFeeRate ?? 0.065) * 100}
                                    onChange={(value) => handleInputChange("checkInFeeRate", value / 100)}
                                />
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="h-fit max-w-full">
                        <CardHeader>
                            <CardTitle>Picture</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex-1 gap-2 ">
                                <div className="space-y-2 mb-2">
                                    <Label htmlFor="ImageUrl">Image Url*</Label>
                                    <Input
                                        id="ImageUrl"
                                        value={formData.imageUrl}
                                        placeholder={"Paste an apartment image's url here"}
                                        onChange={(e) => handleInputChange("imageUrl", e.target.value)}
                                    />
                                </div>
                                <div className="relative aspect-video w-full bg-gray-100 rounded-lg overflow-hidden ">
                                    <img
                                        alt={`${formData.imageUrl} Image`}
                                        src={formData.imageUrl || "https://reviveyouthandfamily.org/wp-content/uploads/2016/11/house-placeholder.jpg"}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            const target = e.target as HTMLImageElement;
                                            target.src = "/api/placeholder/800/600";
                                        }}
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Ownership & Group */}
                    <Card className="h-fit max-w-full">
                        <CardHeader>
                            <CardTitle>Ownership & Group</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-[2fr_1fr_1fr] gap-4">
                                <div className="space-y-2 min-w-0">
                                    <Label>Owners*</Label>
                                    <div className={""}>
                                        <MultiSelect
                                            options={ownerOptions}
                                            staticText={apartmentOwners.map((id) => ownerIdMap.get(id)?.fullName).join(", ")}
                                            selectedValues={apartmentOwners.map(String)}
                                            onChange={(values) => {
                                                const newSelectedOwners = values.map(Number);

                                                setApartmentOwners(newSelectedOwners);
                                            }}
                                            placeholder={"No owners"}
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor={"Owner Group"}>Owner Group*</Label>
                                    <SearchableSelect
                                        variant="outline"
                                        onChange={(value) => handleInputChange("ownerGroupId", Number(value))}
                                        placeholder="Owner Group"
                                        options={ownerGroupOptions}
                                        selectedValue={formData.ownerGroupId.toString()}
                                        width="w-full"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label>Group*</Label>
                                <Select onValueChange={(value) => handleInputChange("groupId", Number(value))}>
                                    <SelectTrigger>
                                        <SelectValue placeholder={groupIdMap.get(formData.groupId)?.name} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Array.from(groupIdMap.entries()).map(([id, group]) => (
                                            <SelectItem key={id} value={id.toString()}>
                                                {group.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label>Tags</Label>
                                <TagMultiSelect
                                    selectedTagIds={formData.tagIds || []}
                                    onChange={(tagIds) => handleInputChange("tagIds", tagIds)}
                                    placeholder="Select tags..."
                                />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Documents & Certificates */}
                    <Card className="h-fit">
                        <CardHeader>
                            <CardTitle>Documents & Certificates</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="grid grid-cols-1 sm:grid-cols-6 gap-4">
                                <div className="space-y-2 col-span-3">
                                    <Label htmlFor="energyCertificateIssuanceDate">Energy Certificate Issuance Date</Label>
                                    <DateInput
                                        id="energyCertificateIssuanceDate"
                                        value={formData.energyCertificateIssuanceDate}
                                        onChange={(date) => handleInputChange("energyCertificateIssuanceDate", date || undefined)}
                                    />
                                </div>

                                <div className={"space-y-2 col-span-3"}>
                                    <Label htmlFor="atak">Short Term Registry ID</Label>
                                    <Input
                                        id=""
                                        value={formData.shortTermRegistryId}
                                        onChange={(e) => handleInputChange("shortTermRegistryId", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2 col-span-3">
                                    <Label htmlFor="initialTaxisRentalAgreement">Initial Rental Contract Taxis</Label>
                                    <Input
                                        id="initialTaxisRentalAgreement"
                                        value={formData.initialTaxisRentalAgreement}
                                        onChange={(e) => handleInputChange("initialTaxisRentalAgreement", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2 col-span-3">
                                    <Label htmlFor="initialTaxisRentalAgreement">Latest Rental Contract Taxis</Label>
                                    <Input
                                        id="latestTaxisRentalAgreement"
                                        value={formData.latestRentalTaxisAgreement}
                                        onChange={(e) => handleInputChange("latestRentalTaxisAgreement", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2 col-span-2">
                                    <Label htmlFor="disinfectionCertificateNumber">Disinfection Certificate Number</Label>
                                    <Input
                                        id="disinfectionCertificateNumber"
                                        value={formData.disinfectionCertificateNumber}
                                        onChange={(e) => handleInputChange("disinfectionCertificateNumber", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2 col-span-2">
                                    <Label htmlFor="disinfectionDate">
                                        Disinfection <br /> Date
                                    </Label>
                                    <DateInput
                                        id="disinfectionDate"
                                        value={formData.disinfectionDate}
                                        onChange={(date) => handleInputChange("disinfectionDate", date || undefined)}
                                    />
                                </div>
                                <div className="space-y-2 col-span-2">
                                    <Label htmlFor="fireExtinguisherRefillCertificateDate">Fire Extinguisher Refill Certificate Date</Label>
                                    <DateInput
                                        id="fireExtinguisherRefillCertificateDate"
                                        value={formData.fireExtinguisherRefillCertificateDate}
                                        onChange={(date) => handleInputChange("fireExtinguisherRefillCertificateDate", date || undefined)}
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Utilities - Part 1 */}
                    <Card className="flex flex-col gap-4 justify-between">
                        <CardHeader>
                            <CardTitle>Internet & ATAK</CardTitle>
                        </CardHeader>
                        <CardContent className={"flex flex-col justify-between gap-4"}>
                            <div className={"flex flex-row gap-4"}>
                                <div className="w-[50%]">
                                    <div>
                                        <Label htmlFor="internet">Internet Company</Label>
                                        <Input
                                            id="internet"
                                            value={formData.internetCompany}
                                            onChange={(e) => handleInputChange("internetCompany", e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="internetLine">Internet Line</Label>
                                        <Input
                                            id="internetLine"
                                            value={formData.internetLine}
                                            onChange={(e) => handleInputChange("internetLine", e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="w-[50%]">
                                    <div>
                                        <Label htmlFor="internet">Internet Username</Label>
                                        <Input
                                            id="internet"
                                            value={formData.internetUsername}
                                            onChange={(e) => handleInputChange("internetUsername", e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="internetLine">InternetPassword</Label>
                                        <Input
                                            id="internetPassword"
                                            value={formData.internetPassword}
                                            onChange={(e) => handleInputChange("internetPassword", e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className={"flex flex-row gap-4"}>
                                <div className={"w-[50%]"}>
                                    <Label htmlFor="atak">ATAK</Label>
                                    <Input id="atak" value={formData.atak} onChange={(e) => handleInputChange("atak", e.target.value)} />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Utilities - Part 2 */}
                    <Card className="h-fit">
                        <CardHeader>
                            <CardTitle>Electricity</CardTitle>
                        </CardHeader>
                        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-6">
                            <div className="space-y-6 md:col-span-1">
                                <Label htmlFor="Electricity Company">Company</Label>
                                <Input
                                    id="Electricity Company"
                                    value={formData.electricityCompany}
                                    onChange={(e) => handleInputChange("electricityCompany", e.target.value)}
                                />
                            </div>
                            <div className="space-y-6 md:col-span-3">
                                <Label htmlFor="electricityRf">RF Payment Code</Label>
                                <Input id="electricityRf" value={formData.electricityRf} onChange={(e) => handleInputChange("electricityRf", e.target.value)} />
                            </div>
                            <div className="space-y-6 md:col-span-2">
                                <Label htmlFor="electricitySupplyId">Supply ID</Label>
                                <Input
                                    id="electricitySupplyId"
                                    value={formData.electricitySupplyId}
                                    onChange={(e) => handleInputChange("electricitySupplyId", e.target.value)}
                                />
                            </div>
                            <div className="space-y-6 md:col-span-2">
                                <Label htmlFor="electricityCertificateTerminationDate">Electricity Certificate Termination Date</Label>
                                <DateInput
                                    id="electricityCertificateTerminationDate"
                                    value={formData.electricityCertificateTerminationDate}
                                    onChange={(date) => handleInputChange("electricityCertificateTerminationDate", date || undefined)}
                                />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Utilities - Part 3 */}
                    <Card className="h-fit">
                        <CardHeader>
                            <CardTitle>Water (EYDAP)</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="eydapSupplyId">Supply ID</Label>
                                    <Input
                                        id="eydapSupplyId"
                                        value={formData.eydapSupplyId}
                                        onChange={(e) => handleInputChange("eydapSupplyId", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="eydapRegistryId">Registry ID</Label>
                                    <Input
                                        id="eydapRegistryId"
                                        value={formData.eydapRegistryId}
                                        onChange={(e) => handleInputChange("eydapRegistryId", e.target.value)}
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="h-fit">
                        <CardHeader>
                            <CardTitle>Insurance Contract</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="insuranceCompany">Insurance Company</Label>
                                    <Input
                                        id="insuranceCompany"
                                        value={formData.insuranceCompany}
                                        onChange={(e) => handleInputChange("insuranceCompany", e.target.value)}
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="insuranceContractId">Insurance Contract ID</Label>
                                    <Input
                                        id="insuranceContractId"
                                        value={formData.insuranceContractId}
                                        onChange={(e) => handleInputChange("insuranceContractId", e.target.value)}
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="h-fit">
                        <CardHeader>
                            <CardTitle>Platform Links</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="airbnbUrl">AirBnB Url</Label>
                                    <Input
                                        id="airbnbUrl"
                                        type="url"
                                        value={formData.airbnbUrl}
                                        onChange={(e) => handleInputChange("airbnbUrl", e.target.value)}
                                        placeholder="https://www.airbnb.com/..."
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="bookingUrl">Booking Url</Label>
                                    <Input
                                        id="bookingUrl"
                                        type="url"
                                        value={formData.bookingUrl}
                                        onChange={(e) => handleInputChange("bookingUrl", e.target.value)}
                                        placeholder="https://www.booking.com/..."
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="h-fit">
                        <CardHeader>
                            <CardTitle>HostHub Mapping</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <HostHubRentalSelector
                                rentalId={formData.hosthubRentalId}
                                excluded={formData.hosthubExcluded ?? false}
                                onChange={(rentalId) => handleInputChange("hosthubRentalId", rentalId)}
                                onExcludedChange={(excluded) => handleInputChange("hosthubExcluded", excluded)}
                                required
                            />
                        </CardContent>
                    </Card>

                    <NeteraAccountSelect
                        value={formData.neteraCredentialsId ?? null}
                        onChange={(id) => handleInputChange("neteraCredentialsId", id)}
                        labels={neteraLabels}
                        isError={neteraLabelsError}
                    />

                    <StrOwnerSelect
                        subject={strSubject}
                        strOwnerId={formData.strOwnerId ?? null}
                        newStrOwner={newStrOwner}
                        owners={strOwnersData?.owners ?? []}
                        onChange={({ subject, strOwnerId, newStrOwner: nextNewOwner }) => {
                            setStrError(null);
                            setStrSubject(subject);
                            setNewStrOwner(nextNewOwner);
                            setFormData((prev) => ({ ...prev, strOwnerId }));
                        }}
                        isLoading={strOwnersLoading}
                        isError={strOwnersError}
                        error={strError}
                    />
                </div>
                <div className="flex justify-end space-x-4">
                    <SaveChangesButton reasonId="new-apartment-save-blocked-bottom" reason={saveBlockedReason} onClick={handleSaveChanges} />
                </div>
            </div>
        </div>
    );
}
